import React from "react";

const ViolenceRiskForm = ({ formData, handleChange }) => {
    const riskItems = [
        {
            id: "violentActs",
            title: "Previous and/or current violence",
            description: "Severe violence refers to physical attack (including with various weapons) towards another individual with intent to inflict severe physical harm. Yes: The individual in question must have committed at least 3 moderately violent aggressive acts or 1 severe violent act. Moderate or less severe aggressive acts such as kicks, blows and shoving that does not cause severe harm to the victim is rated Maybe/moderate."
        },
        {
            id: "violentThreats",
            title: "Previous and/or current threats (verbal/physical)",
            description: "Verbal: Statements, yelling and the like, that involve threat of inflicting other individuals physical harm. Physical: Movements and gestures that warn physical attack."
        },
        {
            id: "substanceMisuse",
            title: "Previous and/or current substance abuse",
            description: "The patient has a history of abusing alcohol, medication and/or other substances (e.g. amphetamine, heroin, cannabis). Abuse of solvents or glue should be included. To rate Yes, the patient must have and/or have had extensive abuse/dependence, with reduced occupational or educational functioning, reduced health and/or reduced participation in leisure activities."
        },
        {
            id: "psychoticSymptoms",
            title: "Previous and/or current major mental illness",
            description: "NB: Whether the patient has or has had a psychotic disorder (e.g. schizophrenia, delusional disorder, psychotic affective disorder). See item 5 to rate personality disorders."
        },
        {
            id: "personalityDisorder",
            title: "Personality disorder",
            description: "Of interest here are eccentric (schizoid, paranoid) and impulsive, uninhibited (emotionally unstable, antisocial) types."
        },
        {
            id: "nonCompliance",
            title: "Shows lack of insight into illness and/or behaviour",
            description: "This refers to the degree to which the patient lacks insight in his/her mental illness, with regard to for instance need of medication, social consequences or behaviour related to illness or personality disorder."
        },
        {
            id: "suspicion",
            title: "Expresses suspicion",
            description: "The patient expresses suspicion towards other individuals either verbally or nonverbally. The person in question appears to be “on guard” towards the environment."
        },
        {
            id: "lackEmpathy",
            title: "Shows lack of empathy",
            description: "The patient appears emotionally cold and without sensitivity towards others’ thoughts or emotional situation."
        },
        {
            id: "unrealisticPlanning",
            title: "Unrealistic planning",
            description: "This assesses to which degree the patient him/herself has unrealistic plans for the future (inside or outside the inpatient unit). Is for instance the patient him/herself realistic with regard to what he/she can expect of support from family and of professional and social network? It is important to assess whether the patient is cooperative and motivated with regard to following plans."
        },
        {
            id: "futureStress",
            title: "Future stress-situations",
            description: "This evaluates the possibility that the patient may be exposed to stress and stressful situations in the future and his/her ability to cope with stress. For example (in and outside inpatient unit): reduced ability to tolerate boundaries, physical proximity to possible victims of violence, substance use, homelessness, spending time in violent environment/association with violent environment, easy access to weapons etc."
        }
    ];

    const options = [
        { value: "no", label: "No", color: "success" },
        { value: "maybe", label: "Maybe / Moderate", color: "warning" },
        { value: "yes", label: "Yes", color: "danger" },
        { value: "unknown", label: "Do not know", color: "secondary" }
    ];

    const overallRiskOptions = [
        { value: "low", label: "Low risk" },
        { value: "moderate", label: "Moderate risk" },
        { value: "high", label: "High risk" }
    ];

    const suggestionOptions = [
        { value: "outpatient", label: "Outpatient follow-up" },
        { value: "increasedMonitoring", label: "Increased monitoring" },
        { value: "admission", label: "Consider admission" },
        { value: "involuntary", label: "Involuntary treatment" }
    ];

    const timingOptions = [
        { value: "admission", label: "At admission" },
        { value: "discharge", label: "At discharge" },
        { value: "clinic", label: "In policlinic" }
    ];

    return (
        <>
            <div className="p-3" style={{ maxHeight: "65vh", overflowY: "auto", scrollbarWidth: "thin" }}>
                {/* Sticky Guidelines Button */}
                <div className="row mb-4 position-sticky top-0 bg-white pt-3" style={{ zIndex: 10 }}>
                    <div className="col-md-3">
                        <button
                            type="button"
                            className="btn btn-outline-primary btn-sm w-100"
                            data-bs-toggle="modal"
                            data-bs-target="#vriskGuidelines"
                        >
                            <i className="ri-information-line me-2"></i>
                            V-RISK-10 Guidelines & Scoring
                        </button>
                    </div>
                </div>

                {/* Timing Selection */}
                <div className="row g-3 mb-5">
                    <div className="col-md-12">
                        <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3 px-4 py-2 text-center">
                            Assessment Timing
                        </small>
                        <div className="d-flex justify-content-center gap-5 flex-wrap">
                            {timingOptions.map((opt) => (
                                <div className="form-check" key={opt.value}>
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="assessmentTiming"
                                        id={`timing_${opt.value}`}
                                        value={opt.value}
                                        checked={formData.assessmentTiming === opt.value}
                                        onChange={handleChange}
                                    />
                                    <label className="form-check-label fw-medium" htmlFor={`timing_${opt.value}`}>
                                        {opt.label}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Patient Info */}
                <div className="row g-3 mb-5">
                    <div className="col-md-6">
                        <div className="form-floating form-floating-outline">
                            <input
                                type="text"
                                className="form-control"
                                name="patientName"
                                value={formData.patientName || ""}
                                onChange={handleChange}
                                readOnly={!!formData.patientId}
                            />
                            <label>Patient Name</label>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-floating form-floating-outline">
                            <input
                                type="date"
                                className="form-control"
                                name="date"
                                value={formData.date || ""}
                                onChange={handleChange}
                            />
                            <label>Date</label>
                        </div>
                    </div>
                </div>

                {/* V-RISK-10 Items */}
                <div className="accordion" id="vriskAccordion">
                    {riskItems.map((item, index) => (
                        <div className="accordion-item mb-3 shadow-sm rounded" key={item.id}>
                            <h2 className="accordion-header d-flex align-items-center">
                                <button
                                    className={`accordion-button fw-semibold ${index !== 0 ? "collapsed" : ""}`}
                                    type="button"
                                    data-bs-toggle="collapse"
                                    data-bs-target={`#collapse-${item.id}`}
                                >
                                    <span className="me-3 fw-bold">{index + 1}.</span>
                                    {item.title}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm rounded-circle"
                                    style={{ width: "32px", height: "32px" }}
                                    data-bs-toggle="modal"
                                    data-bs-target={`#info-${item.id}`}
                                >
                                    <i className="ri-information-line"></i>
                                </button>
                            </h2>

                            <div
                                id={`collapse-${item.id}`}
                                className={`accordion-collapse collapse ${index === 0 ? "show" : ""}`}
                            >
                                <div className="accordion-body pt-4">
                                    <div className="row g-4 justify-content-center">
                                        {options.map((opt) => (
                                            <div className="col-lg-3 col-md-6" key={opt.value}>
                                                <div className={`form-check text-${opt.color}`}>
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name={item.id}
                                                        id={`${item.id}_${opt.value}`}
                                                        value={opt.value}
                                                        checked={formData[item.id] === opt.value}
                                                        onChange={handleChange}
                                                    />
                                                    <label className="form-check-label fw-medium" htmlFor={`${item.id}_${opt.value}`}>
                                                        {opt.label}
                                                    </label>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Individual Item Modal */}
                            <div className="modal fade" id={`info-${item.id}`} tabIndex="-1">
                                <div className="modal-dialog modal-dialog-centered">
                                    <div className="modal-content">
                                        <div className="modal-header">
                                            <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block px-4 text-center mb-4">
                                                {index + 1}. {item.title}
                                            </small>
                                            <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
                                        </div>
                                        <div className="modal-body">
                                            <p>{item.description}</p>
                                        </div>
                                        <div className="modal-footer">
                                            <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">
                                                Close
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Overall Clinical Evaluation */}
                <div className="mt-5 pt-4 border-top">
                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-4 px-4 text-center">
                        Overall Clinical Evaluation
                    </small>

                    <div className="row g-5 mb-5">
                        <div className="col-md-12">
                            <small className="text-dark bg-secondary-subtle rounded-4 fw-bold d-block px-4 text-center mb-4">
                                How great do you think the violence risk is for this patient?
                            </small>
                            <div className="d-flex justify-content-center gap-5 flex-wrap">
                                {overallRiskOptions.map((opt) => (
                                    <div className="form-check" key={opt.value}>
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="overallRisk"
                                            id={`risk_${opt.value}`}
                                            value={opt.value}
                                            checked={formData.overallRisk === opt.value}
                                            onChange={handleChange}
                                        />
                                        <label className="form-check-label fw-medium" htmlFor={`risk_${opt.value}`}>
                                            {opt.label}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="col-md-12">
                            <small className="text-dark bg-secondary-subtle rounded-4 fw-bold d-block px-4 text-center mb-4">
                                Suggested action following overall clinical evaluation:
                            </small>
                            <div className="d-flex justify-content-center gap-5 flex-wrap">
                                {suggestionOptions.map((opt) => (
                                    <div className="form-check" key={opt.value}>
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="suggestedAction"
                                            id={`action_${opt.value}`}
                                            value={opt.value}
                                            checked={formData.suggestedAction === opt.value}
                                            onChange={handleChange}
                                        />
                                        <label className="form-check-label fw-medium" htmlFor={`action_${opt.value}`}>
                                            {opt.label}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                </div>

                {/* Main Guidelines Modal */}
                <div className="modal fade" id="vriskGuidelines" tabIndex="-1">
                    <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">V-RISK-10 Guidelines & Scoring Instructions</h5>
                                <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
                            </div>
                            <div className="modal-body">
                                <p><strong>Scoring Instructions:</strong></p>
                                <p>The rater collects information about each of the ten risk factors on the V-RISK-10 checklist. Put a check in the box to indicate the degree of likelihood that the risk factor applies to the patient:</p>
                                <ul>
                                    <li><strong>No:</strong> Does not apply to this patient</li>
                                    <li><strong>Maybe/moderate:</strong> Maybe applies / present to a moderately severe degree</li>
                                    <li><strong>Yes:</strong> Definitely applies to a severe degree</li>
                                    <li><strong>Do not know:</strong> Too little information to answer</li>
                                </ul>

                                <p className="mt-4"><strong>Overall Clinical Evaluation:</strong></p>
                                <p>Based on clinical judgement, other available information and the checklist:</p>
                                <ul>
                                    <li>How great do you think the violence risk is for this patient?</li>
                                    <li>Suggested action following overall clinical evaluation</li>
                                </ul>

                                <p className="mt-3"><strong>Note:</strong> Justifications/reasons/arguments should be detailed in patient record and/or discharge summary.</p>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ViolenceRiskForm;